import type { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'

type Page = Record<string, any>
type ImageProvenance = Record<string, boolean>
const isRecord = (value: unknown): value is Page =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

export interface ProductPagePresentation {
  shop: number
  requireExistingBuilder: boolean
  homeBuilder: (base?: MenuPageBuilder) => MenuPageBuilder
  pageBuilder: (page: Page, site: MenuPageBuilder, menuId?: number, brandId?: number, count?: number) => MenuPageBuilder
  homeMetadata?: Record<string, string>
  initialImageProvenance?: (source: ImageProvenance) => ImageProvenance
  markImage: (src: string, images: ImageProvenance) => void
}

/** Shared presentation boundary. Catalog records, filters, variants and prices
 * retain their original references; each template supplies only its policy and
 * editorial content. The no-metadata fallback is deliberately explicit. */
export const createProductPagePresentation = (options: ProductPagePresentation) =>
  (response: unknown, request: unknown): unknown => {
    if (!isRecord(request) || Number(request.shop) !== options.shop || request.includeMetadata === false
      || !isRecord(response) || !isRecord(response.filterProducts)) return response
    if (options.requireExistingBuilder
      && !response.sitePageBuilder && !response.menu?.pageBuilder && !response.brand?.pageBuilder) return response

    const site = options.homeBuilder(response.sitePageBuilder)
    const next: Page = { ...response, sitePageBuilder: site }
    if (request.homePage) {
      const menu = isRecord(response.menu) ? response.menu : {}
      const missingMetadata = Object.fromEntries(Object.entries(options.homeMetadata || {}).filter(([key]) =>
        typeof menu[key] !== 'string' || !menu[key].trim()
      ))
      // Editorial metadata maintained in the backend remains authoritative.
      next.menu = { ...menu, ...missingMetadata, pageBuilder: site }
    } else {
      if (isRecord(response.menu)) next.menu = {
        ...response.menu,
        pageBuilder: options.pageBuilder(response.menu, site,
          Number(request.menuId || response.menu.id), Number(request.brandId) || undefined, response.filterProducts.count),
      }
      if (isRecord(response.brand)) next.brand = {
        ...response.brand,
        pageBuilder: options.pageBuilder(response.brand, site,
          undefined, Number(request.brandId || response.brand.id), response.filterProducts.count),
      }
    }
    // A shared menu may be used by multiple shops. Only its selected shop's
    // explicit editor hero can replace that template's presentation.
    if (!request.homePage && !request.brandId && next.menu?.pageBuilder) {
      const hero = response.menu?.pageBuilder?.shopHeroes?.[String(options.shop)]
      if (hero) next.menu.pageBuilder = { ...next.menu.pageBuilder, hero }
    }
    const sourceImages = response.landingPageImageAiGenerated || {}
    const images = options.initialImageProvenance
      ? options.initialImageProvenance(sourceImages) : { ...sourceImages }
    const visit = (value: unknown) => {
      if (Array.isArray(value)) value.forEach(visit)
      else if (isRecord(value)) Object.entries(value).forEach(([key, child]) => {
        if ((key === 'imageSrc' || key === 'mobileImageSrc') && typeof child === 'string') options.markImage(child, images)
        else if (child && typeof child === 'object') visit(child)
      })
    }
    ;[next.sitePageBuilder, next.menu?.pageBuilder, next.brand?.pageBuilder].forEach(visit)
    next.landingPageImageAiGenerated = images
    return next
  }
